<template>
  <BaseBanner
    v-if="showPaymentMethodBanner"
    @bannerClosed="closeBanner"
    data-ec="invalid_payment_method_banner">
    <template v-slot:title>
      {{ $t('paymentMethodsBanner.actionRequired') }}
    </template>
    <template v-slot:message>
      <span id="invalid-payment-methods-banner-admin-text">
        {{ $t('paymentMethodsBanner.addAMethod', {separator: $t('banner.separator')}) }}
      </span>
      <AddPaymentMethodModal
        dd-scope="appLevelBannerDD"
        :show="showPaymentMethodModal"
        id="add-payment-method-modal"
        @close="closeAddPaymentMethodModal"
      />
    </template>
    <template v-slot:cta>
      <BaseButton
        title="add now"
        v-if="canAddPaymentMethods"
        id="invalid-payment-methods-banner-add-method-button"
        @click.native="openAddPaymentMethodModal"
        size="small"
        data-ec="invalid_payment_method_banner"
        data-et="open_add_payment_method_modal"
        variant="primary">
        <span>{{ $t('paymentMethodsBanner.addNow') }}</span>
      </BaseButton>
    </template>
  </BaseBanner>
</template>

<script>
import { mapGetters } from 'vuex';
import BaseBanner from '@/components/library/banners/BaseBanner.vue';
import AddPaymentMethodModal from '@/views/modals/payment/AddPaymentMethodModal.vue';
import BaseButton from '@/components/library/buttons/BaseButton.vue';

export default {
  name: 'InvalidPaymentMethodBanner',
  components: {
    BaseBanner,
    AddPaymentMethodModal,
    BaseButton,
  },
  data() {
    return {
      showPaymentMethodModal: false,
      paymentMethodBannerDismissed: false,
      paymentMethodBannerDataLoading: true,
      showIntegrationsBanner: false,
    };
  },
  computed: {
    ...mapGetters({
      currentPlan: 'plans/current',
      usage: 'metering/usage',
      paymentMethods: 'paymentMethods/results',
      defaultCardHasExpired: 'paymentMethods/defaultCardHasExpired',
      user: 'user/user',
      isAuthenticated: 'auth/isAuthenticated',
      company: 'company/company',
      userHasRole: 'roles/userHasRole',
      mfaActionRequired: 'userMfa/mfaActionRequired',
    }),
    canAddPaymentMethods() {
      return this.userHasRole('super-admin');
    },
    isOnSubscription() {
      if (!this.currentPlan) {
        return false;
      }
      return !['pay-as-you-go', 'free-trial'].includes(this.currentPlan.id);
    },
    isOnNewBilling() {
      if (!this.company) {
        return null;
      }
      return !!(this.company.new_billing_enabled);
    },
    hasNoValidPaymentMethod() {
      return !!(!this.paymentMethods.length || this.defaultCardHasExpired);
    },
    isPayingManually() {
      return this.usage?.collection_method === 'send_invoice';
    },
    showPaymentMethodBanner() {
      return !!(
        this.isAuthenticated
        && !this.paymentMethodBannerDataLoading
        && this.isAdmin
        && this.isOnNewBilling
        && !this.paymentMethodBannerDismissed
        && this.isOnSubscription
        && !this.isPayingManually
        && this.hasNoValidPaymentMethod
      );
    },
    isAdmin() {
      return this.userHasRole('super-admin');
    },
  },

  methods: {
    async checkAuthenticationAndData() {
      if (!this.isAuthenticated || this.mfaActionRequired) return;

      this.paymentMethodBannerDataLoading = true;

      if (this.isAdmin === null) {
        await this.$store.dispatch('user/GET');
      }

      const allDispatches = [() => Promise.resolve()];

      if (this.isAdmin && this.paymentMethods.length === 0) {
        allDispatches.push(() => Promise.resolve(this.$store.dispatch('paymentMethods/GET')));
        if (!this.currentPlan) {
          allDispatches.push(() => Promise.resolve(this.$store.dispatch('plans/GET_CURRENT_PLAN')));
        }
      }

      await Promise.all(allDispatches.map(f => f())).then(() => {
        this.paymentMethodBannerDataLoading = false;
      });
    },
    startPaymentMethodBannerReopenTimer() {
      this.paymentMethodBannerDismissed = true;
      setTimeout(() => {
        this.paymentMethodBannerDismissed = false;
      }, 172800000);
    },
    openAddPaymentMethodModal() {
      this.showPaymentMethodModal = true;
    },
    closeAddPaymentMethodModal() {
      this.showPaymentMethodModal = false;
    },
    closeBanner() {
      this.startPaymentMethodBannerReopenTimer();
    },
  },
  watch: {
    isAuthenticated() {
      this.paymentMethodBannerDismissed = false;
      this.checkAuthenticationAndData();
    },
  },
  mounted() {
    this.checkAuthenticationAndData();
  },
};
</script>
